// app/javascript/components/ChatBox.js
import { useRef, useEffect, useLayoutEffect, useState, useCallback } from "react";
import { Box } from "@mui/material";
import { keyframes } from "@mui/system";
import "../../stylesheets/index.css";
import "../../stylesheets/chat_area.css";
import "../../stylesheets/message.css";
import ChatButton from "./Reusable/ChatButton";
import ChatMessage from "./Chat/ChatMessage";
import get_message_date from "./Chat/MessageDate";
import { useNavigate } from "react-router-dom";
import { isDarkMode, themeColors } from "../utils/constants";
import { ShareModal, ShareButtonChat } from "../utils/share";
import PrintOutText from "./Chat/PrintOutText";
import { SignInApple, SignInGoogle } from "./NewAccountPage";
import { useAppContext } from "../contexts/AppContext";
import { useChatContext } from "../contexts/ChatContext";
import ScrollDown from "./Reusable/ScrollDown";
import { getThreadMessages } from "../api/message";
import { useMemo } from "react";
import NewLogoLightMode from '../images/NewLogoLightMode.png'
import NewLogoDarkMode from '../images/NewLogoDarkMode.png'

const orbPulsate = keyframes`
  0%   { opacity: 0.18; transform: scale(1); }
  50%  { opacity: 0.42; transform: scale(1.06); }
  100% { opacity: 0.18; transform: scale(1); }
`;

const INITIAL_SCROLL_IMAGE_WAIT_MS = 150//220;
const INITIAL_SCROLL_STABLE_MAX_FRAMES = 24;
const INITIAL_SCROLL_SETTLE_DELAY_MS = 80;
const INITIAL_SCROLL_FOLLOW_UP_MS = 90;//130;
const INITIAL_SCROLL_FAILSAFE_MS = 2200;

// Build a stable, content-agnostic key (won't change during streaming)
function stableMessageKey(m, threadKey = "") {
  if (m?.__stableKey) return m.__stableKey;
  if (m?.id)   return `id:${m.id}`;
  if (m?.uuid) return `uuid:${m.uuid}`;

  // Use createdAt + sender within a thread when no id/uuid yet.
  // Avoid using message text or attachments: those can change mid-stream.
  const ts = m?.createdAt ? Date.parse(m.createdAt) : NaN;
  if (Number.isFinite(ts)) {
    return `ts:${threadKey}:${m?.sender || ""}:${ts}`;
  }

  // Last-resort (ephemeral) — stable only for this runtime; used
  // before the server assigns id/uuid/createdAt.
  if (!m.__localKey) {
    m.__localKey = `tmp:${threadKey}:${Math.random().toString(36).slice(2)}`;
  }
  return m.__localKey;
}

// Factory that memoizes DOM-safe ids for stable keys
function makeDomIdFactory(threadKey = "") {
  const keyToId = new Map();
  const used = new Set();
  const gen = () => {
    let v;
    do { v = `m-${Math.random().toString(36).slice(2, 10)}`; } while (used.has(v));
    used.add(v);
    return v;
  };
  return (m) => {
    // Streaming messages carry __stableKey so their DOM id survives
    // factory rebuilds (e.g. temp → resolved thread UUID transition).
    if (m?.__stableKey) return m.__stableKey;

    const k = stableMessageKey(m, threadKey);
    let id = keyToId.get(k);
    if (!id) {
      // If we have a real backend key, reuse a sanitized form directly.
      if (k.startsWith("id:") || k.startsWith("uuid:")) {
        id = k.replace(/[:]/g, "-"); // DOM-safe
      } else {
        id = gen();
      }
      keyToId.set(k, id);
    }
    return id;
  };
}



const ChatBox = (props) => {
  const { appProps } = useAppContext();

  const {
    sidebarOffset,
    isDesktop,
    messageThreads,
    onQuestionSubmit,
    onBeginEditMessage,
    loadingResponse,
    userData,
    setUserData,
    chatRef,
    chatContainerRef,
    chatMessagesContainerRef,
    preventQuestionInput,
    setPreventQuestionInput,
    showLoadingWaitingMessage,
    setShowLoadingWaitingMessage,
    useFullWidth,
    // showNewUserMessage,
    isSecondaryExperience,
    isNewUUID,
    loadingUserData,
    setIsSecondaryExperience,
    showSignInButtons,
    setQuestionOnDemand,
    anchorOffsetTop,
    editingMessage,
  } = props;

  const {
    experienceMessageThreads,
    showScrollDown,
    setShowScrollDown,
    secondaryExperienceType,
    questionOnDemand,
    freeUnlimitedOnly,
    hasShownFreeUnlimitedMessage,
    activeThread,
    loadingMessages,
    loadingPhaseMessage,
    setThreadViewLoading,
    scrollToMessageId,
    setScrollToMessageId,
  } = useChatContext();

  const {
    responseFlow,
    setMessageThreads,
    showChatShare,
    setShowChatShare,
  } = appProps;

  const navigate = useNavigate();

  const [transitionFlowButtonsAmount, setTransitionFlowButtonsAmount] =
    useState(0);
  const [transitionFlowRegenAmount, setTransitionFlowRegenAmount] = useState(0);
  // const [testVal, setTestVal] = useState(null);
  const chatMessageRef = useRef();
  const [loadingShowHistory, setLoadingShowHistory] = useState(true); //useState(true)
  const [modalVisible, setModalVisible] = useState(false);
  const [showInitialMesageFlow, setShowInitialMesageFlow] = useState(false);
  const [displayChatBox, setDisplayChatBox] = useState(true); //useState(false)
  const [showButon1, setShowButon1] = useState(0);
  const [showButon2, setShowButon2] = useState(0);
  const [showMore, setShowMore] = useState(0);
  const [showOrb, setShowOrb] = useState(0);
  const [hasRefreshedButtons, setHasRefreshedButtons] = useState(false);
  // const [canSeeWhy, setCanSeeWhy] = useState(0);
  // const [isSelected, setIsSelected] = useState("");
  const [errors, setErrors] = useState([]);
  const [messageThreadLoaded, setMessageThreadLoaded] = useState(false);
  const [needsHistory, setNeedsHistory] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const topSentinelRef = useRef(null);
  const [topInView, setTopInView] = useState(false);
  const loadingOlderRef = useRef(false);

  const stickyElRef = useRef(null);
  const padElRef = useRef(null);
  const suppressScrollRef = useRef(0);     
  const lastAtBottomRef = useRef(null);
  const autoStickBottomRef = useRef(true);
  const anchorTargetRef = useRef(null);
  const messageRefs = useRef(new Map());
  const lastAnchoredQuestionKeyRef = useRef(null);
  const activeAnchorMessageKeyRef = useRef(null);
  const followAnchorDuringLoadRef = useRef(false);
  const didInitialScroll = useRef(false)
  const prevThreadUuidRef = useRef(null);
  const skipNextBottomPinRef = useRef(false);
  const loadingResponseRef = useRef(Boolean(loadingResponse));
  const chatLoadPerfRef = useRef({
    threadUuid: null,
    startedAtMs: 0,
    revealed: false,
  });

  const bottomSentinelRef = useRef(null);
  const initialScrollDoneRef = useRef(false);

  // const clientKeyMap = useRef(new WeakMap()).current;

  const [hideChatThread, setHideChatThread] = useState(true)

  useEffect(() => {
    loadingResponseRef.current = Boolean(loadingResponse);
  }, [loadingResponse]);

  // const domIdFor = useCallback((m) => {
  //   //Set stable id for pinning user message to the top
  //   if (m.id) return m.id;
  //   if (m.uuid) return m.uuid;
  //   const existing = clientKeyMap.get(m);
  //   if (existing) return existing;
  //   const k = `c-${Math.random().toString(36).slice(2)}`;
  //   clientKeyMap.set(m, k);
  //   return k;
  // }, [clientKeyMap]);

  const domIdFor = useMemo(
    () => makeDomIdFactory(activeThread?.threadUuid || "global"),
    [activeThread?.threadUuid]
  );

  const isDirectThread = Boolean(activeThread?.pairedThreadId);
  const shouldRenderQuestion1Button =
    Boolean(isSecondaryExperience) ||
    Boolean(responseFlow?.question1?.[0] && responseFlow?.question1?.[1]);
  const shouldRenderQuestion2Button =
    Boolean(isSecondaryExperience) ||
    Boolean(responseFlow?.question2?.[0] && responseFlow?.question2?.[1]);
  const chatLoadPerfDebug = useMemo(() => {
    if (typeof window === "undefined") return false;
    try {
      return window.localStorage?.getItem("chat_load_perf_debug") === "1";
    } catch {
      return false;
    }
  }, []);

  const pushChatLoadMetric = useCallback(
    (event, extra = {}) => {
      if (typeof window === "undefined" || typeof performance === "undefined") return;
      const perfEnabled =
        chatLoadPerfDebug || window.__chatLoadMetricsEnabled === true;
      if (!perfEnabled) return;
      const perf = chatLoadPerfRef.current || {};
      const now = performance.now();
      const elapsedMs = perf?.startedAtMs
        ? Math.round(now - perf.startedAtMs)
        : null;
      const metric = {
        timestamp: new Date().toISOString(),
        event,
        threadUuid: perf?.threadUuid || activeThread?.threadUuid || null,
        elapsedMs,
        ...extra,
      };
      if (!Array.isArray(window.__chatLoadMetrics)) {
        window.__chatLoadMetrics = [];
      }
      window.__chatLoadMetrics.push(metric);
      if (window.__chatLoadMetrics.length > 300) {
        window.__chatLoadMetrics.shift();
      }
      if (chatLoadPerfDebug) {
        console.debug("[ChatLoadPerf]", metric);
      }
    },
    [activeThread?.threadUuid, chatLoadPerfDebug],
  );

  const beginChatLoadMetric = useCallback(
    (threadUuid, reason) => {
      if (!threadUuid || threadUuid.startsWith("temp-")) return;
      if (typeof performance === "undefined") return;
      chatLoadPerfRef.current = {
        threadUuid,
        startedAtMs: performance.now(),
        revealed: false,
      };
      pushChatLoadMetric("start", { reason });
    },
    [pushChatLoadMetric],
  );

  const revealChatLoadMetric = useCallback(
    (source, extra = {}) => {
      const perf = chatLoadPerfRef.current;
      if (!perf?.startedAtMs || perf?.revealed) return;
      chatLoadPerfRef.current = {
        ...(perf || {}),
        revealed: true,
      };
      pushChatLoadMetric("revealed", { source, ...extra });
    },
    [pushChatLoadMetric],
  );


  const setMsgRef = useCallback(
    (id) => (el) => {
      if (el) {
        messageRefs.current.set(id, el);
      } else {
        messageRefs.current.delete(id);
      }
    },
    []
  );


  let newUserQuestion1;
  let newUserQuestion2;
  if (secondaryExperienceType == "new-user") {
    newUserQuestion1 = "Best Tech Stock?";
    newUserQuestion2 = "Crypto To Buy?";
  } else if (secondaryExperienceType == "ask-share") {
    newUserQuestion1 = "Share Incite App";
    newUserQuestion2 = "Continue using Incite";
  }


  const handleScrollDown = useCallback(
    (e) => {
      if (suppressScrollRef.current > 0 || anchorTargetRef.current != null) return;
      const el = e.currentTarget; 
      if (loadingResponseRef.current && followAnchorDuringLoadRef.current) {
        followAnchorDuringLoadRef.current = false;
        activeAnchorMessageKeyRef.current = null;
        lastAnchoredQuestionKeyRef.current = null;
        setMessageThreads((prev) => {
          if (!Array.isArray(prev) || prev.length === 0) return prev;
          const next = prev.slice();
          for (let idx = next.length - 1; idx >= 0; idx -= 1) {
            const msg = next[idx];
            if (msg?.sender !== "human") continue;
            if (!msg?.shouldAnchor) break;
            next[idx] = {
              ...msg,
              shouldAnchor: false,
            };
            return next;
          }
          return prev;
        });
      }
      const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight <= 200; // so it has to be 100px up to be at the bottom
      if (!atBottom) {
        // user intentionally moved off-bottom; stop auto locking until they return
        autoStickBottomRef.current = false;
      } else {
        autoStickBottomRef.current = true;
      }
      if (lastAtBottomRef.current !== atBottom) {
        lastAtBottomRef.current = atBottom;
        setShowScrollDown(!atBottom);
      }
    },
    [setMessageThreads, setShowScrollDown]
  );


  const handleRegenerateSubmit = async (question = null) => {
    if (showSignInButtons) {
      navigate("/register");
    }

    setTransitionFlowButtonsAmount(0.0);
    setTransitionFlowRegenAmount(0.0);

    // Allow new buttons instead of default ones (only relevent for new users)
    setHasRefreshedButtons(true);

    setTimeout(() => {
      setTransitionFlowButtonsAmount(0.99);
      setTransitionFlowRegenAmount(0.99);
    }, 150);
    // dispatch to server with these new questions
    // server will update table to "attach" new questions
    // to that last chat message. It will update the row

    if (chatMessageRef.current) {
      // chatMessageRef.current.scrollIntoView({ behavior: "smooth" });
    }
  };


  const isLoggedIn = !!userData.user_id;
  const waitForImages = useCallback(async (maxWaitMs = INITIAL_SCROLL_IMAGE_WAIT_MS) => {
    const root = chatMessagesContainerRef.current;
    if (!root) return;
    const imgs = Array.from(root.querySelectorAll('img'));
    if (!imgs.length) return;
    await Promise.race([
      Promise.allSettled(
      imgs.map(
        (img) =>
          img.complete
            ? Promise.resolve()
            : new Promise((res) => {
                const done = () => {
                  img.removeEventListener('load', done);
                  img.removeEventListener('error', done);
                  res();
                };
                img.addEventListener('load', done);
                img.addEventListener('error', done);
              })
      )
    ),
      new Promise((resolve) => setTimeout(resolve, maxWaitMs)),
    ]);
  }, [chatMessagesContainerRef]);

  const waitForStableHeight = useCallback(async (el, { stableFrames = 2, maxFrames = INITIAL_SCROLL_STABLE_MAX_FRAMES } = {}) => {
    let last = -1, stable = 0, frames = 0;
    while (stable < stableFrames && frames < maxFrames) {
      await new Promise((r) => requestAnimationFrame(r));
      const h = el.scrollHeight;
      if (Math.abs(h - last) < 1) stable += 1; else stable = 0;
      last = h;
      frames += 1;
    }
  }, []);

  // Reset the "initial scroll" gate when the active thread changes
  useLayoutEffect(() => {
    const threadUuid = activeThread?.threadUuid;
    const prevThreadUuid = prevThreadUuidRef.current;
    const isTempToRealResolve =
      Boolean(prevThreadUuid?.startsWith("temp-")) &&
      Boolean(threadUuid) &&
      !threadUuid.startsWith("temp-") &&
      Boolean(loadingResponseRef.current);

    initialScrollDoneRef.current = false;
    if (isTempToRealResolve) {
      didInitialScroll.current = true;
      initialScrollDoneRef.current = true;
      skipNextBottomPinRef.current = true;
      pushChatLoadMetric("skip-hide", {
        reason: "temp-to-real-resolve",
        fromThreadUuid: prevThreadUuid,
        toThreadUuid: threadUuid,
      });
      setHideChatThread(false);
      revealChatLoadMetric("skip-hide-temp-to-real-resolve");
      return;
    }
    if (!threadUuid || threadUuid.startsWith("temp-") || isSecondaryExperience) {
      pushChatLoadMetric("skip-hide", {
        reason: !threadUuid
          ? "no-thread"
          : threadUuid.startsWith("temp-")
            ? "temp-thread"
            : "secondary-experience",
      });
      setHideChatThread(false);
      revealChatLoadMetric("skip-hide");
      return;
    }
    // Always hide real-thread content on thread switch; reveal only after bottom-lock.
    beginChatLoadMetric(threadUuid, "thread-switch");
    setHideChatThread(true);
  }, [
    activeThread?.threadUuid,
    isSecondaryExperience,
    beginChatLoadMetric,
    pushChatLoadMetric,
    revealChatLoadMetric,
  ]);

  useEffect(() => {
    if (isSecondaryExperience) {
      setHideChatThread(false);
      revealChatLoadMetric("secondary-experience");
    }
  }, [isSecondaryExperience, revealChatLoadMetric])

  useEffect(() => {
    setThreadViewLoading(Boolean(!isSecondaryExperience && hideChatThread));
  }, [hideChatThread, isSecondaryExperience, setThreadViewLoading]);


  // Failsafe: if initial scroll logic stalls, force bottom then reveal.
  useEffect(() => {
    if (!hideChatThread) return;
    const timeoutId = setTimeout(() => {
      if (!hideChatThread) return;
      const el = chatContainerRef.current;
      if (el) {
        el.scrollTop = el.scrollHeight;
      }
      didInitialScroll.current = true;
      initialScrollDoneRef.current = true;
      lastAtBottomRef.current = true;
      setShowScrollDown(false);
      setHideChatThread(false);
      revealChatLoadMetric("failsafe-timeout", {
        timeoutMs: INITIAL_SCROLL_FAILSAFE_MS,
      });
    }, INITIAL_SCROLL_FAILSAFE_MS);

    return () => clearTimeout(timeoutId);
  }, [
    activeThread?.threadUuid,
    hideChatThread,
    setShowScrollDown,
    revealChatLoadMetric,
  ]);

  useEffect(() => {
    loadingOlderRef.current = false;
    autoStickBottomRef.current = true;
    lastAtBottomRef.current = true;
    lastAnchoredQuestionKeyRef.current = null;
    activeAnchorMessageKeyRef.current = null;
    followAnchorDuringLoadRef.current = false;
    setHasMore(true);
    setTopInView(false);
  }, [activeThread?.threadUuid]);

  useEffect(() => {
    const curr = activeThread?.threadUuid ?? null;
    const prev = prevThreadUuidRef.current;
    if (prev === curr) return; // only run if thread uuid has changed..
    const isTempToRealResolve =
      Boolean(prev?.startsWith("temp-")) &&
      Boolean(curr) &&
      !curr.startsWith("temp-") &&
      Boolean(loadingResponseRef.current);
    didInitialScroll.current = isTempToRealResolve
      ? true
      : Boolean(curr?.startsWith("temp-"));
    prevThreadUuidRef.current = curr;
  }, [activeThread?.threadUuid]);


  // helper: set pad to exactly the underfill so content hugs the bottom
  const adjustBottomPad = useCallback(() => {
    const scroller = chatContainerRef.current;
    const pad = padElRef.current;
    const contentRoot = chatMessagesContainerRef.current;
    if (!scroller || !pad || !contentRoot) return;
    const currentGap = scroller.clientHeight - (scroller.scrollHeight - scroller.scrollTop);
    let new_height = pad.offsetHeight + currentGap
    if (new_height < 0) new_height = 0
    pad.style.height = `${new_height}px`;
  }, [chatContainerRef, chatMessagesContainerRef, padElRef, lastAtBottomRef]);

  
  useEffect(() => {
    if (loadingResponse) return;
    if (padElRef.current) {
      setTimeout(() => {
        requestAnimationFrame(adjustBottomPad);
      }, 24) // slight delay here is important.
    }
  }, [loadingResponse]);

  // Do the first-time scroll precisely when ready, then unhide
  useLayoutEffect(() => {
    if (didInitialScroll.current == true) return;
    const scroller = chatContainerRef.current;
    if (!scroller) return;
    if (!chatContainerRef.current) return;
    if (scrollToMessageId) return;
    if (!showInitialMesageFlow || !messageThreadLoaded) return;
    if (initialScrollDoneRef.current) return;

    let cancelled = false;
    const followUpTimers = [];
    const run = async () => {
      suppressScrollRef.current++;
      if (!chatContainerRef.current) return;
      try {
        pushChatLoadMetric("initial-scroll-run", {
          messageCount: Array.isArray(messageThreads) ? messageThreads.length : 0,
        });
        await waitForImages(INITIAL_SCROLL_IMAGE_WAIT_MS);
        pushChatLoadMetric("initial-scroll-after-images");
        await waitForStableHeight(scroller);
        pushChatLoadMetric("initial-scroll-after-stable-height-1");
        // Short settle pass for late-rendering blocks without adding long fixed latency.
        await new Promise((resolve) => setTimeout(resolve, INITIAL_SCROLL_SETTLE_DELAY_MS));
        await waitForStableHeight(scroller);
        pushChatLoadMetric("initial-scroll-after-stable-height-2");
        if (cancelled) return;
        
        const forceBottom = () => {
          const el = chatContainerRef.current;
          if (!el || cancelled) return;
          el.scrollTop = el.scrollHeight;
        };

        forceBottom();
        // One short follow-up pass, then reveal.
        followUpTimers.push(
          setTimeout(() => {
            forceBottom();
            if (cancelled) return;
            setHideChatThread(false); // reveal only after the final bottom-lock pass
            revealChatLoadMetric("initial-scroll-followup", {
              followUpDelayMs: INITIAL_SCROLL_FOLLOW_UP_MS,
            });
          }, INITIAL_SCROLL_FOLLOW_UP_MS),
        );
        didInitialScroll.current = true;

        lastAtBottomRef.current = true;
        setShowScrollDown(false);
        initialScrollDoneRef.current = true;

      } finally {
        suppressScrollRef.current--;
      }
    };

    run();
    return () => {
      cancelled = true;
      followUpTimers.forEach((id) => clearTimeout(id));
    };
  }, [
    activeThread?.threadUuid,
    initialScrollDoneRef.current,
    chatContainerRef.current,
    showInitialMesageFlow,
    messageThreadLoaded,
    loadingResponse,
    waitForImages,
    waitForStableHeight,
    messageThreads?.length,
    pushChatLoadMetric,
    revealChatLoadMetric,
  ]);

  // Keep the active thread pinned to the bottom while layout is still settling
  // (charts/images can resize after initial render).
  useEffect(() => {
    const threadUuid = activeThread?.threadUuid;
    if (!threadUuid || threadUuid.startsWith("temp-")) return;
    if (scrollToMessageId) return;
    if (skipNextBottomPinRef.current) {
      skipNextBottomPinRef.current = false;
      return;
    }

    const scroller = chatContainerRef.current;
    const contentRoot = chatMessagesContainerRef.current;
    if (!scroller || !contentRoot) return;

    autoStickBottomRef.current = true;
    let rafId = null;

    const syncBottom = () => {
      const el = chatContainerRef.current;
      if (!el) return;
      const remaining = el.scrollHeight - el.scrollTop - el.clientHeight;
      const nearBottom = remaining <= 220;
      if (!autoStickBottomRef.current && !nearBottom && !lastAtBottomRef.current) {
        return;
      }
      suppressScrollRef.current++;
      el.scrollTop = el.scrollHeight;
      suppressScrollRef.current--;
      lastAtBottomRef.current = true;
      setShowScrollDown(false);
    };

    const scheduleSync = () => {
      if (rafId) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(syncBottom);
    };

    scheduleSync();
    const settleTimers = [80, 220, 520].map((ms) =>
      setTimeout(scheduleSync, ms),
    );
    const releaseTimerId = setTimeout(() => {
      autoStickBottomRef.current = false;
    }, 1400);

    let resizeObserver;
    if (typeof ResizeObserver !== "undefined") {
      resizeObserver = new ResizeObserver(scheduleSync);
      resizeObserver.observe(contentRoot);
      resizeObserver.observe(scroller);
    }

    return () => {
      settleTimers.forEach((id) => clearTimeout(id));
      clearTimeout(releaseTimerId);
      if (rafId) cancelAnimationFrame(rafId);
      if (resizeObserver) resizeObserver.disconnect();
    };
  }, [
    activeThread?.threadUuid,
    scrollToMessageId,
    chatContainerRef,
    chatMessagesContainerRef,
    setShowScrollDown,
  ]);

  useEffect(() => {
    if (userData && userData.uuid) {
      if (Object.keys(userData).length) {
        //} && (isLoggedIn || !userData.uuidHasAccount)) {
        setLoadingShowHistory(false);
        setNeedsHistory(true); // this will cause the trigger to get the history.
        // getMessageHistory(
        //   appProps.setHasShownHistory,
        //   true,
        //   setLoadingUserMessages
        // );
      } else {
        setNeedsHistory(false);
      }
    }
  }, [userData && userData.uuid]); // So this only fires once, and only when the user data is there (for pre account or user account)

  // useEffect(() => {
  //   if (!loadingResponse) {
  //     if (
  //       messageThreads.length > 1 &&
  //       messageThreads[messageThreads.length - 2].message != "Explain why?"
  //     ) {
  //       setCanSeeWhy(0.99);
  //     } else {
  //       setCanSeeWhy(0);
  //     }
  //   }
  // }, [messageThreads, loadingResponse]);

  // useEffect(() => {
  //   if (isSelected != "") {
  //     setIsSelected("");
  //   }
  // }, [loadingResponse]);

  const executeScroll = () => {
    if (chatMessageRef.current) {
      chatMessageRef.current.scrollIntoView({ behavior: "smooth" });
    }
  };

  let countSinceShowMessageTime = 0;

  useEffect(() => {
    if (responseFlow.question1[0]) {
      setTransitionFlowButtonsAmount(0.99);
    } else {
      setTransitionFlowButtonsAmount(0);
    }
  }, [responseFlow.question1[0]]);

  const loadOlderMessages = useCallback(
    async (thread) => {
      const threadUuid = thread?.threadUuid;
      if (!threadUuid || threadUuid.startsWith("temp-")) return;
      if (!hasMore || loadingOlderRef.current) return;
      if (activeThread?.threadUuid !== threadUuid) return;

      const el = chatContainerRef.current;
      if (!el) return;

      const prevTop = el.scrollTop;
      const prevHeight = el.scrollHeight;
      const before = messageThreads.length ? messageThreads[0].createdAt : null;
      loadingOlderRef.current = true;

      try {
        const older = await getThreadMessages({ threadUuid, limit: 6, before });
        if (activeThread?.threadUuid !== threadUuid) return;
        if (!Array.isArray(older) || older.length === 0) {
          setHasMore(false);
          return;
        }

        setMessageThreads((prev) => {
          const merged = [...older, ...prev];
          const seen = new Set();
          return merged.filter((m) => {
            const key = m.id || m.uuid || m.createdAt;
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
          });
        });
        if (older.length < 6) {
          setHasMore(false);
        }

        // Preserve viewport when prepending older messages.
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            const node = chatContainerRef.current;
            if (!node || activeThread?.threadUuid !== threadUuid) return;
            const nextHeight = node.scrollHeight;
            suppressScrollRef.current++;
            node.scrollTop = Math.max(0, prevTop + (nextHeight - prevHeight));
            suppressScrollRef.current--;
            const hasOverflow = (node.scrollHeight - node.clientHeight) > 1;
            const remaining =
              node.scrollHeight - node.scrollTop - node.clientHeight;
            const atBottom = !hasOverflow ? true : remaining <= 200;
            lastAtBottomRef.current = atBottom;
            setShowScrollDown(hasOverflow && !atBottom);
          });
        });
      } finally {
        loadingOlderRef.current = false;
      }
    },
    [
      activeThread?.threadUuid,
      chatContainerRef,
      hasMore,
      messageThreads,
      setMessageThreads,
      setShowScrollDown,
    ],
  );

  const scrollLastHumanToTop = useCallback(() => {
    const scroller = chatContainerRef.current;
    if (padElRef.current) {
      padElRef.current.style.height = "0px";
    }
    if (!scroller || !messageThreads?.length) return;
    const anchoredHuman = [...messageThreads]
      .reverse()
      .find((message) => message?.sender === "human" && message?.shouldAnchor);
    if (!anchoredHuman) return;

    const id = domIdFor(anchoredHuman);
    const el = messageRefs.current.get(id);
    if (!el) return;

    // Compute where we WANT the scroller to end up
    const sr = scroller.getBoundingClientRect();
    const er = el.getBoundingClientRect();
    const offset = (anchorOffsetTop || 0);

    const desiredTop = scroller.scrollTop + (er.top - sr.top) - offset;
    const maxTop = scroller.scrollHeight - scroller.clientHeight;

    // If we can’t reach desiredTop because we’re at maxTop, add bottom pad
    const needed = Math.max(0, desiredTop - maxTop);
    if (needed > 0 && padElRef.current) {
      padElRef.current.style.height = `${Math.ceil(needed)}px`;
    }
    // Now perform the exact jump
    suppressScrollRef.current++;
    scroller.scrollTop = desiredTop;
    suppressScrollRef.current--;

    lastAtBottomRef.current = false;

    const hasOverflow = (scroller.scrollHeight - scroller.clientHeight) > 1; // epsilon
    const remaining = scroller.scrollHeight - scroller.scrollTop - scroller.clientHeight;
    const atBottom = !hasOverflow ? true : remaining <= 200; // same threshold you use elsewhere
    lastAtBottomRef.current = atBottom;
    setShowScrollDown(hasOverflow && !atBottom);

}, [chatContainerRef, messageThreads, domIdFor, anchorOffsetTop, setShowScrollDown]);

  useLayoutEffect(() => {
    if (!loadingResponse) return;
    const anchoredHuman = [...(messageThreads || [])]
      .reverse()
      .find((message) => message?.sender === "human" && message?.shouldAnchor);
    if (!anchoredHuman) return;

    const anchorMessageKey = [
      activeThread?.threadUuid || "no-thread",
      anchoredHuman.id || anchoredHuman.uuid || anchoredHuman.createdAt || anchoredHuman.message || "",
    ].join(":");

    if (activeAnchorMessageKeyRef.current !== anchorMessageKey) {
      activeAnchorMessageKeyRef.current = anchorMessageKey;
      followAnchorDuringLoadRef.current = true;
    }

    if (!followAnchorDuringLoadRef.current) return;

    const anchorKey = [
      anchorMessageKey,
      showLoadingWaitingMessage ?? "no-waiting",
      loadingPhaseMessage || "no-phase",
    ].join(":");
    if (lastAnchoredQuestionKeyRef.current === anchorKey) return;
    lastAnchoredQuestionKeyRef.current = anchorKey;

    // Wait a tick so refs/layout are ready
    requestAnimationFrame(scrollLastHumanToTop);
  }, [
    loadingResponse,
    messageThreads,
    activeThread?.threadUuid,
    showLoadingWaitingMessage,
    loadingPhaseMessage,
    scrollLastHumanToTop,
  ]);

  const scrollToMessage = useCallback(() => {
    if (!scrollToMessageId) return;
    const scroller = chatContainerRef.current;
    if (padElRef.current) {
      padElRef.current.style.height = "0px";
    }
    if (!scroller || !messageThreads?.length) return;
    const target = messageThreads.find(
      (m) => String(m.id) === String(scrollToMessageId)
    );
    if (!target) return;
    initialScrollDoneRef.current = true;
    didInitialScroll.current = true;
    const id = domIdFor(target);
    const el = messageRefs.current.get(id);
    if (!el) return;

    const sr = scroller.getBoundingClientRect();
    const er = el.getBoundingClientRect();
    const offset = (anchorOffsetTop || 0);
    const desiredTop = scroller.scrollTop + (er.top - sr.top) - offset;
    const maxTop = scroller.scrollHeight - scroller.clientHeight;
    const needed = Math.max(0, desiredTop - maxTop);
    if (needed > 0 && padElRef.current) {
      padElRef.current.style.height = `${Math.ceil(needed)}px`;
    }
    suppressScrollRef.current++;
    scroller.scrollTop = desiredTop;
    suppressScrollRef.current--;

    lastAtBottomRef.current = false;
    const hasOverflow = (scroller.scrollHeight - scroller.clientHeight) > 1;
    const remaining = scroller.scrollHeight - scroller.scrollTop - scroller.clientHeight;
    const atBottom = !hasOverflow ? true : remaining <= 200;
    lastAtBottomRef.current = atBottom;
    setShowScrollDown(hasOverflow && !atBottom);
    setScrollToMessageId(null);
    setHideChatThread(false);
    revealChatLoadMetric("scroll-to-message");
  }, [
    anchorOffsetTop,
    chatContainerRef,
    domIdFor,
    messageThreads,
    scrollToMessageId,
    setScrollToMessageId,
    setShowScrollDown,
    setHideChatThread,
    revealChatLoadMetric,
  ]);

  useLayoutEffect(() => {
    if (!scrollToMessageId) return;
    requestAnimationFrame(scrollToMessage);
  }, [scrollToMessageId, scrollToMessage]);


  // This is for the history!
  useEffect(() => {
    if (!activeThread || !messageThreads || messageThreads.length <= 1) return;
    const root = chatContainerRef.current;
    const target = topSentinelRef.current;
    if (!root || !target) return;

    let observer = null;
    let cancelled = false;
    const timeoutId = setTimeout(() => {
      if (cancelled) return;
      observer = new IntersectionObserver(
        ([entry]) => setTopInView(entry.isIntersecting),
        {
          root, // observe within the scroll container
          threshold: 0.01, // treat as visible as soon as even 1% shows
          rootMargin: "0px",
        },
      );
      observer.observe(target);
    }, 250);

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
      if (observer) observer.disconnect();
    };
  }, [activeThread?.threadUuid, messageThreads?.length, chatContainerRef]);

  useEffect(() => {
    if (!userData?.user_account_info) return;
    if (!topInView) return;
    if (!messageThreads || messageThreads.length <= 1) return;
    if (!hasMore) return;
    if (!activeThread?.threadUuid) return;
    if (!initialScrollDoneRef.current) return;
    if (lastAtBottomRef.current === true) return;

    // Only paginate when user has scrolled up near top.
    loadOlderMessages(activeThread);
  }, [
    userData?.user_account_info,
    topInView,
    messageThreads?.length,
    hasMore,
    activeThread?.threadUuid,
    loadOlderMessages,
  ]);

  // critical to showing the data
  useEffect(() => {
    if (!loadingUserData) {
      // ensure no longer loading user state before rendering chat box
      setShowInitialMesageFlow(true);
      if (!isLoggedIn) {
        // if user is not logged in; can render immediately
        setDisplayChatBox(true);
      }
    }
  }, [loadingUserData, isLoggedIn]);

  useEffect(() => {
    if (loadingMessages) return;
    pushChatLoadMetric("loading-messages-complete", {
      messageCount: Array.isArray(messageThreads) ? messageThreads.length : 0,
    });
  }, [loadingMessages, messageThreads?.length, pushChatLoadMetric]);

  useEffect(() => {
    if (isSecondaryExperience != null) {
      if (isSecondaryExperience) {
        setShowButon1(0);
        setShowButon2(0);
        if (isDarkMode) {
          setTimeout(() => setShowButon1(0.99), 2666); //4000)
          setTimeout(() => setShowButon2(0.99), 3000); //4500)
          setTimeout(() => setShowMore(0.99), 2533); //3800)
          setTimeout(() => setShowOrb(0.99), 1933); //2900)
          setTimeout(() => setShowOrb(0), 7333); //11000)
        } else {
          setTimeout(() => setShowButon1(0.99), 2066); //3100)
          setTimeout(() => setShowButon2(0.99), 2400); //3600)
          setTimeout(() => setShowMore(0.99), 1933); //2900)
        }
      } else {
        setShowButon1(responseFlow?.question1?.[0] ? 0.99 : 0);
        setShowButon2(responseFlow?.question2?.[0] ? 0.99 : 0);
        setShowMore(responseFlow?.text ? 0.99 : 0);
      }
    }
  }, [
    isSecondaryExperience,
    responseFlow?.question1?.[0],
    responseFlow?.question2?.[0],
    responseFlow?.text,
  ]);


  useEffect(() => {
    if (needsHistory == true) {
      if (messageThreads && messageThreads.length > 1) {
        // must have rendered more than just starting message.
        setMessageThreadLoaded(true);
      }
    } else if (needsHistory == false) {
      setMessageThreadLoaded(true); // no messages to low, allow render
    }
  }, [messageThreads, needsHistory]);

  useEffect(() => {
    if (!messageThreadLoaded) return;
    pushChatLoadMetric("message-thread-loaded", {
      messageCount: Array.isArray(messageThreads) ? messageThreads.length : 0,
      loadingMessages: Boolean(loadingMessages),
    });
  }, [
    messageThreadLoaded,
    messageThreads?.length,
    loadingMessages,
    pushChatLoadMetric,
  ]);

  if (!isSecondaryExperience && (!messageThreads || !messageThreads.map || messageThreads.length === 0)) {
    return (
      <Box
        className="chatbox-div-container3"
        sx={{
          backgroundColor: themeColors.chatBoxColor,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flex: 1,
        }}
      >
        <Box
          component="img"
          src={isDarkMode ? NewLogoDarkMode : NewLogoLightMode}
          alt="Incite logo"
          sx={{ maxWidth: 170, width: "50%", opacity: 0.1, pointerEvents: "none", userSelect: "none" }}
        />
      </Box>
    );
  }

  return (
    <Box
      className="chatbox-div-container1"
      sx={{ backgroundColor: themeColors.chatBoxColor }}
    >
      {!isSecondaryExperience && hideChatThread && (
        <Box
          className="chatbox-div-container3"
          sx={{
            backgroundColor: "transparent",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            pointerEvents: "none",
            zIndex: 2,
          }}
        >
          <Box
            component="img"
            src={isDarkMode ? NewLogoDarkMode : NewLogoLightMode}
            alt="Incite logo"
            sx={{
              maxWidth: 170,
              width: "50%",
              pointerEvents: "none",
              userSelect: "none",
              animation: `${orbPulsate} 2s ease-in-out infinite`,
              filter: isDarkMode
                ? "brightness(0) invert(1) drop-shadow(0 0 24px rgba(255,255,255,0.22))"
                : "brightness(1.18) contrast(1.08) drop-shadow(0 0 20px rgba(255,255,255,0.85))",
            }}
          />
        </Box>
      )}
      <Box
        className="chatbox-div-container3"
        sx={{
          backgroundColor: themeColors.chatBoxColor,

          ...(isDesktop && {
            left: `${sidebarOffset}px`,
            width: `calc(100% - ${sidebarOffset}px)`,
          }),
        }}
        ref={chatRef}
      >
        {showInitialMesageFlow && (
          <Box
            style={{
              width: "100%",
              display: "flex",
              flexDirection: "row",
              justifyContent: "center",
            }}
          >
            <Box
              className="chatbox-scroll-container"
              sx={{
                backgroundColor: themeColors.chatBoxColor, minHeight: 0,
                width: '100%',
                opacity: hideChatThread ? 0 : 1,
                transition: hideChatThread ? 'none' : 'opacity 0.35s ease-in',
              }}
              ref={chatContainerRef}
              onScroll={handleScrollDown}
            >
              <Box
                className="inner-chatbox-scroll-container"
                sx={{
                  backgroundColor: themeColors.chatBoxColor,
                  width: '100%',
                }}
                ref={chatMessagesContainerRef}
              >
                <Box
                  sx={{
                    paddingBottom: "30px",
                    maxWidth: "100%",
                    minWidth: '100%',
                    margin: "auto",
                    position: "relative",
                  }}
                >
                  <div ref={topSentinelRef} style={{ height: 1 }} />

                  { isSecondaryExperience && experienceMessageThreads[0] && messageThreads.length == 0 && 
                     <>
                      {secondaryExperienceType == "new-user" ? (
                        <PrintOutText
                          showDownArrowIcon={true}
                          message={
                            experienceMessageThreads[0].message
                          }
                          lineSpacing={[303, 250, 114]}
                        />
                      ) : (
                        <PrintOutText
                          showDownArrowIcon={true}
                          message={
                            experienceMessageThreads[0].message
                          }
                          lineSpacing={[329, 233, 100]}
                        />
                      )}
                    </>
                  }

                  { messageThreads.map((mt, idx) => {
                      const isLastHuman = mt.sender === "human" && messageThreads.slice().reverse().findIndex(x => x.sender === "human") === (messageThreads.length - 1 - idx);
                      let marginVal;
                      if (
                        idx == 0 ||
                        messageThreads[idx].sender ==
                          messageThreads[idx - 1].sender
                      ) {
                        marginVal = "8px";
                      } else if (messageThreads[idx].isButton) {
                        marginVal = "17px";
                      } else {
                        marginVal = "17px";
                      }
                      let isLastMessage = idx == messageThreads.length - 1;
                      const { date_value, show_message_time } =
                        get_message_date(
                          messageThreads,
                          countSinceShowMessageTime,
                          idx
                        );
                      let styles = {};
                      if (idx == 0) {
                        styles = { marginTop: "12px", marginBottom: "16px" };
                      }

                      const msgId = domIdFor(mt);
                      const shouldStick = loadingResponse && isLastHuman;
                      
                      return (
                        <Box
                          key={msgId}
                          ref={setMsgRef(msgId)}
                          sx={{
                            position: "static",//shouldStick ? "sticky" : "static",
                            top: shouldStick ? '0px' : "unset",
                            zIndex: shouldStick ? 2 : undefined,
                          }}
                        >
                          {!(idx == 0 || isSecondaryExperience) &&
                            ((show_message_time && idx > 2) || idx == 0) && (
                              <Box
                                className="chatbox-time"
                                sx={{
                                  ...styles,
                                  color: isDarkMode ? "#606773" : "#9BABC2",
                                }}
                              >
                                {date_value}
                              </Box>
                            )}

                          {!isSecondaryExperience && (
                            <Box
                              ref={setMsgRef(msgId)}
                              sx={{
                                marginTop: marginVal,
                                display: "flex",
                                flexDirection: "column",
                                position: "relative",
                              }}
                            >
                              <ChatMessage
                                msgId={msgId}
                                idx={idx}
                                messageThread={mt}
                                priorMessageThread={(idx >= 1) ? messageThreads[idx - 1] : null}
                                loadingResponse={loadingResponse}
                                isLastMessage={isLastMessage}
                                showLoadingWaitingMessage={
                                  showLoadingWaitingMessage
                                }
                                setShowLoadingWaitingMessage={
                                  setShowLoadingWaitingMessage
                                }
                                useFullWidth={useFullWidth}
                                setModalVisible={setModalVisible}
                                setShowChatShare={setShowChatShare}
                                questionOnDemand={questionOnDemand}
                                setQuestionOnDemand={setQuestionOnDemand}
                                preventQuestionInput={preventQuestionInput}
                                setPreventQuestionInput={
                                  setPreventQuestionInput
                                }
                                onQuestionSubmit={onQuestionSubmit}
                                secondaryExperienceType={
                                  secondaryExperienceType
                                }
                                isLoggedIn={isLoggedIn}
                                freeUnlimitedOnly={freeUnlimitedOnly}
                                hasShownFreeUnlimitedMessage={
                                  hasShownFreeUnlimitedMessage
                                }
                                onBeginEditMessage={onBeginEditMessage}
                                suppressMessageActions={isDirectThread}
                              />
                            </Box>
                          )}
                        </Box>
                      );
                    })}
                    
                    <div
                      ref={padElRef}
                      data-anchor-pad
                      style={{ height: 0, width: 1, flex: '0 0 auto', pointerEvents: 'none' }}
                    />

                  {!isSecondaryExperience &&
                    !showChatShare &&
                    !loadingResponse && (
                      <Box
                        sx={{
                          marginTop: "24px",
                          marginBottom: "24px",
                          width: "100%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      ></Box>
                    )}

                  {!isSecondaryExperience ? (
                    <Box sx={{}}></Box>
                  ) : (
                    <Box
                      sx={{ marginBottom: isDarkMode ? "72px" : "20px" }}
                    ></Box>
                  )}

                  {showChatShare && (
                    <>
                      <ShareModal
                        modalVisible={modalVisible}
                        setModalVisible={setModalVisible}
                      />
                      <ShareButtonChat
                        setShowChatShare={setShowChatShare}
                        transitionFlowButtonsAmount={
                          transitionFlowButtonsAmount
                        }
                        setModalVisible={setModalVisible}
                      />
                    </>
                  )}

                  {showSignInButtons ? (
                    <Box
                      sx={{
                        marginBottom: "20px",
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        alignItems: "center",
                      }}
                    >
                      <SignInApple
                        userData={userData}
                        setUserData={setUserData}
                        setErrors={setErrors}
                        onChatPage={true}
                      />
                      <SignInGoogle
                        userData={userData}
                        setUserData={setUserData}
                        setErrors={setErrors}
                        onChatPage={true}
                      />
                    </Box>
                  ) : (
                    <>
                      {shouldRenderQuestion1Button &&
                        !isDirectThread &&
                        !showChatShare &&
                        !editingMessage && (
                        <Box
                          sx={{
                            marginTop: "4px",
                            opacity: showButon1,
                            transition: "opacity 1s ease",
                          }}
                        >
                          <ChatButton
                            questionNumber={1}
                            //q={(isSecondaryExperience && !hasRefreshedButtons) ? newUserQuestion1 : responseFlow.question1[1]}
                            q={
                              isSecondaryExperience
                                ? newUserQuestion1
                                : responseFlow.question1[1]
                            }
                            onQuestionSubmit={onQuestionSubmit}
                            transitionFlowButtonsAmount={
                              transitionFlowButtonsAmount
                            }
                            preventQuestionInput={preventQuestionInput}
                            setPreventQuestionInput={setPreventQuestionInput}
                            //isSecondaryExperience={(isSecondaryExperience && !hasRefreshedButtons)}
                            isSecondaryExperience={isSecondaryExperience}
                            secondaryExperienceType={secondaryExperienceType}
                            userData={userData}
                            setUserData={setUserData}
                            setIsSecondaryExperience={setIsSecondaryExperience}
                            setModalVisible={setModalVisible}
                            setShowChatShare={setShowChatShare}
                            handleRegenerateSubmit={handleRegenerateSubmit}
                          />
                        </Box>
                      )}
                      {shouldRenderQuestion2Button &&
                        !isDirectThread &&
                        !showChatShare &&
                        !editingMessage && (
                        <Box
                          ref={chatMessageRef}
                          className={
                            isSecondaryExperience &&
                            secondaryExperienceType == "ask-share"
                              ? "bottom-button-placement"
                              : ""
                          }
                          sx={{
                            marginTop: "4px",
                            opacity: showButon2,
                            transition: "opacity 1s ease",
                          }}
                        >
                          <ChatButton
                            questionNumber={2}
                            // q={(isSecondaryExperience && !hasRefreshedButtons) ? newUserQuestion2 : responseFlow.question2[1]}
                            q={
                              isSecondaryExperience
                                ? newUserQuestion2
                                : responseFlow.question2[1]
                            }
                            onQuestionSubmit={onQuestionSubmit}
                            transitionFlowButtonsAmount={
                              transitionFlowButtonsAmount
                            }
                            preventQuestionInput={preventQuestionInput}
                            setPreventQuestionInput={setPreventQuestionInput}
                            // isSecondaryExperience={(isSecondaryExperience && !hasRefreshedButtons)}
                            isSecondaryExperience={isSecondaryExperience}
                            secondaryExperienceType={secondaryExperienceType}
                            userData={userData}
                            setUserData={setUserData}
                            setIsSecondaryExperience={setIsSecondaryExperience}
                            setModalVisible={setModalVisible}
                            setShowChatShare={setShowChatShare}
                            handleRegenerateSubmit={handleRegenerateSubmit}
                          />
                        </Box>
                      )}
                    </>
                  )}
                  <ScrollDown
                    isDesktop={isDesktop}
                    sidebarOffset={sidebarOffset}
                    showScrollDown={showScrollDown}
                    executeScroll={executeScroll}
                  /> 
                  <div ref={bottomSentinelRef} style={{ height: 1 }} />
                </Box>
              </Box>
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default ChatBox;
